import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import adminApi, { STATIC_ORIGIN } from "./adminApi";
import SendMailModal from "./SendMailModal";
import mkcgLogo from "../../assests/logo/logo.png"; // adjust the path if your logo is elsewhere
import {
  Search,
  X,
  Pencil,
  Eye,
  Mail,
  Trash2,
  Loader2,
  UserCircle2,
  Download,
  RotateCcw,
  Printer,
  ExternalLink,
  BadgeCheck,
} from "lucide-react";

// Gender stays fixed (1 = Male, 2 = Female).
// ID proof type, blood group, reservation category and sub-category are now loaded
// from the database through GET /admin/student-registrations/meta
// (see meta.idProofTypes, meta.bloodGroups, meta.reservationCategories, meta.subCategories).
const GENDERS = [
  { value: "1", label: "Male" },
  { value: "2", label: "Female" },
  { value: "3", label: "Other" },
];

const STATES = [
  "Odisha", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi",
];
const STATE_OPTIONS = STATES.map((s) => ({ value: s, label: s }));

// const ADMISSION_YEARS = Array.from({ length: 12 }, (_, i) => 2026 - i);
const currentYear = new Date().getFullYear();

const ADMISSION_YEARS = Array.from(
  { length: 12 },
  (_, i) => currentYear - i
);
const YEAR_OPTIONS = ADMISSION_YEARS.map((y) => ({ value: String(y), label: String(y) }));

function fileUrl(filename) {
  if (!filename) return null;
  return `${STATIC_ORIGIN}/uploads/students/${filename}`;
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Registration state helpers ──────────────────────────────────────────────
//  Incomplete : status 2, confirm 0  → student has not submitted Step 2
//  Pending    : status 2, confirm 1  → Step 2 submitted, waiting for admin
//  Active     : status 1             → confirmed by admin (locked)
const isActive = (s) => Number(s.status) === 1;
const isIncomplete = (s) => Number(s.confirm) !== 1 && !isActive(s);
const isPending = (s) => Number(s.confirm) === 1 && !isActive(s);

const statusLabel = (s) =>
  isActive(s) ? "Active" : isPending(s) ? "Pending" : "Incomplete";

const statusClass = (s) =>
  isActive(s)
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    : isPending(s)
    ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
    : "bg-slate-100 text-slate-500 ring-1 ring-slate-200";

function toISODate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  return dt.toISOString().slice(0, 10);
}

// Default filter window: last 1 month
function defaultToDate() {
  return new Date().toISOString().slice(0, 10);
}

function defaultFromDate() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
}

const emptyEditForm = {
  st_name: "",
  st_gender: "",
  st_idprf_type: "",
  st_aadharno: "",
  st_course: "",
  st_department: "",
  st_admsn_category: "",
  st_admsn_othr_categry: "",
  admission_year: "",
  st_rollno: "",
  st_dob: "",
  st_bloodgroup: "",
  father_name: "",
  mother_name: "",
  category: "",
  sub_catagory: "",
  father_occupation: "",
  mother_occupation: "",
  pre_address: "",
  pre_state: "",
  pre_pin: "",
  per_address: "",
  per_state: "",
  per_pin: "",
  st_phno: "",
  st_mailid: "",
  landline_parent: "",
  phno_parent: "",
  mailid_parent: "",
  st_examination: "",
  passing_year: "",
  college_name: "",
  university_name: "",
  remarks: "",
  secexam_type: "",
  secpass_year: "",
  sec_col_name: "",
  sec_board: "",
  sec_remark: "",
};

// ── Small building blocks ───────────────────────────────────────────────────
const Field = ({ label, req, children, span }) => (
  <div className={span === 2 ? "sm:col-span-2" : undefined}>
    <label className="text-xs font-medium text-slate-500 mb-1 block">
      {label}
      {req && <span className="text-red-500"> *</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition";

const SectionTitle = ({ children }) => (
  <h5 className="text-xs font-semibold uppercase tracking-wide text-[#0EA5A4] mb-3">
    {children}
  </h5>
);

// Professional icon button used for every table action
const ACTION_TONES = {
  blue:   "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-600 hover:text-white hover:border-blue-600",
  amber:  "text-amber-600 bg-amber-50 border-amber-100 hover:bg-amber-500 hover:text-white hover:border-amber-500",
  indigo: "text-indigo-600 bg-indigo-50 border-indigo-100 hover:bg-indigo-600 hover:text-white hover:border-indigo-600",
  teal:   "text-teal-600 bg-teal-50 border-teal-100 hover:bg-teal-600 hover:text-white hover:border-teal-600",
  green:  "text-green-600 bg-green-50 border-green-100 hover:bg-green-600 hover:text-white hover:border-green-600",
  red:    "text-red-600 bg-red-50 border-red-100 hover:bg-red-600 hover:text-white hover:border-red-600",
  slate:  "text-slate-300 bg-slate-50 border-slate-100",
};

const ActionBtn = ({ title, tone = "slate", onClick, disabled, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-all duration-150 shadow-sm hover:shadow disabled:cursor-not-allowed disabled:shadow-none ${ACTION_TONES[tone]} ${
      disabled && tone !== "slate" ? "opacity-50" : ""
    }`}
  >
    {children}
  </button>
);

export default function ManageStudentRegistration() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [meta, setMeta] = useState({
    departments: [],
    admissionCategories: [],
    otherCategories: [],
    courses: [],
    senderMail: "", // Company Email ID from tbl_parameters (parid = 2)
    bloodGroups: [],            // ← DYNAMIC: blood_groups table
    idProofTypes: [],           // ← DYNAMIC: tbl_parameters (param_type = 'ID')
    reservationCategories: [],  // ← DYNAMIC: reservation_categories table
    subCategories: [],          // ← DYNAMIC: sub_categories table
  });

  // Filters
  const [nameQ, setNameQ] = useState("");
  const [courseQ, setCourseQ] = useState("");
  const [yearQ, setYearQ] = useState("");
  const [fromDate, setFromDate] = useState(defaultFromDate());
  const [toDate, setToDate] = useState(defaultToDate());

  const [toast, setToast] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Mail modal (the form itself lives in SendMailModal.jsx)
  const [mailStudent, setMailStudent] = useState(null);

  const [autoPrint, setAutoPrint] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchMeta = async () => {
    try {
      const res = await adminApi.get("/admin/student-registrations/meta");
      setMeta((prev) => ({ ...prev, ...res.data }));
    } catch (err) {
      console.error("Could not load student registration meta lists:", err);
    }
  };

  const fetchStudents = async (overrides = {}) => {
    setLoading(true);
    setError("");

    const params = {
      q: overrides.nameQ ?? nameQ,
      course: overrides.courseQ ?? courseQ,
      year: overrides.yearQ ?? yearQ,
      fromDate: overrides.fromDate ?? fromDate,
      toDate: overrides.toDate ?? toDate,
    };

    try {
      const res = await adminApi.get("/admin/student-registrations", { params });
      setStudents(res.data.students || []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load student registrations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    fetchStudents();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStudents();
  };

  const handleReset = () => {
    const from = defaultFromDate();
    const to = defaultToDate();

    setNameQ("");
    setCourseQ("");
    setYearQ("");
    setFromDate(from);
    setToDate(to);

    fetchStudents({ nameQ: "", courseQ: "", yearQ: "", fromDate: from, toDate: to });
  };

  // ── Activate ─────────────────────────────────────────────────────────────
  const handleActivate = async (item, e) => {
    e.stopPropagation();

    if (isIncomplete(item)) {
      showToast("error", "This registration is still incomplete — Step 2 hasn't been submitted yet.");
      return;
    }

    if (isActive(item)) return;

    const ok = window.confirm("Are you sure you want to activate this registration?");
    if (!ok) return;

    setActivatingId(item.reg_id);

    try {
      const res = await adminApi.patch(`/admin/student-registrations/${item.reg_id}/activate`);

      setStudents((prev) =>
        prev.map((s) =>
          s.reg_id === item.reg_id
            ? {
                ...s,
                status: res.data.status,
                confirm: 1,
                referance_number: res.data.referance_number,
                activate_date: res.data.activate_date,
              }
            : s
        )
      );

      showToast("success", `Activated — Registration ID: ${res.data.referance_number}`);
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not activate this registration.");
    } finally {
      setActivatingId(null);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async (item, e) => {
    e?.stopPropagation();

    const ok = window.confirm(
      `Are you sure you want to permanently delete the registration of "${
        item.st_name || "this student"
      }"?`
    );
    if (!ok) return;

    setDeletingId(item.reg_id);

    try {
      await adminApi.delete(`/admin/student-registrations/${item.reg_id}`);
      setStudents((prev) => prev.filter((s) => s.reg_id !== item.reg_id));
      showToast("success", "Student registration deleted.");
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete this registration.");
    } finally {
      setDeletingId(null);
    }
  };

  // ── Mail modal ───────────────────────────────────────────────────────────
  const openMailModal = (student, e) => {
    e?.stopPropagation();
    setMailStudent(student);
  };

  const closeMailModal = () => setMailStudent(null);

  const openViewAndPrint = (regId) => {
    setAutoPrint(true);
    openView(regId);
  };

  // ── Export to Excel ──────────────────────────────────────────────────────
  const handleExport = () => {
    if (students.length === 0) {
      showToast("error", "Nothing to export.");
      return;
    }

    const headers = [
      "Sl No",
      "Student Name",
      "Gender",
      "Course",
      "Year of Admission",
      "Created Date",
      "Registration ID",
      "Status",
    ];

    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

    const rows = students.map((s, i) => [
      i + 1,
      s.st_name,
      s.gender_name,
      s.course_name,
      s.admission_year,
      fmtDate(s.created_date),
      s.referance_number,
      statusLabel(s),
    ]);

    const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `student-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ── Edit modal ───────────────────────────────────────────────────────────
  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [existingFiles, setExistingFiles] = useState({});
  const [newFiles, setNewFiles] = useState({});

  const idProofRef = useRef(null);
  const photoRef = useRef(null);
  const signatureRef = useRef(null);

  const openEdit = async (regId) => {
    setEditId(regId);
    setShowEdit(true);
    setEditLoading(true);
    setEditError("");
    setNewFiles({});

    try {
      const res = await adminApi.get(`/admin/student-registrations/${regId}`);
      const s = res.data.student;

      setEditForm({
        st_name: s.st_name || "",
        st_gender: s.st_gender || "",
        st_idprf_type: s.st_idprf_type || "",
        st_aadharno: s.st_aadharno || "",
        st_course: s.st_course || "",
        st_department: s.st_department || "",
        st_admsn_category: s.st_admsn_category || "",
        st_admsn_othr_categry: s.st_admsn_othr_categry || "",
        admission_year: s.admission_year || "",
        st_rollno: s.st_rollno || "",
        st_dob: toISODate(s.st_dob),
        st_bloodgroup: s.st_bloodgroup || "",
        father_name: s.father_name || "",
        mother_name: s.mother_name || "",
        category: s.category || "",
        sub_catagory: s.sub_catagory || "",
        father_occupation: s.father_occupation || "",
        mother_occupation: s.mother_occupation || "",
        pre_address: s.pre_address || "",
        pre_state: s.pre_state || "",
        pre_pin: s.pre_pin || "",
        per_address: s.per_address || "",
        per_state: s.per_state || "",
        per_pin: s.per_pin || "",
        st_phno: s.st_phno || "",
        st_mailid: s.st_mailid || "",
        landline_parent: s.landline_parent || "",
        phno_parent: s.phno_parent || "",
        mailid_parent: s.mailid_parent || "",
        st_examination: s.st_examination || "",
        passing_year: s.passing_year || "",
        college_name: s.college_name || "",
        university_name: s.university_name || "",
        remarks: s.remarks || "",
        secexam_type: s.secexam_type || "",
        secpass_year: s.secpass_year || "",
        sec_col_name: s.sec_col_name || "",
        sec_board: s.sec_board || "",
        sec_remark: s.sec_remark || "",
      });

      setExistingFiles({
        idProofDoc: s.st_idproof_img,
        passportPhoto: s.st_photo,
        signature: s.st_signature,
      });
    } catch (err) {
      setEditError(err.response?.data?.message || "Could not load this registration.");
    } finally {
      setEditLoading(false);
    }
  };

  const closeEdit = () => {
    if (editSaving) return;
    setShowEdit(false);
  };

  const setF = (key, value) => setEditForm((prev) => ({ ...prev, [key]: value }));

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!editForm.st_name.trim()) {
      setEditError("Student Name is required.");
      return;
    }

    setEditError("");
    setEditSaving(true);

    const fd = new FormData();
    Object.entries(editForm).forEach(([k, v]) => fd.append(k, v ?? ""));

    if (newFiles.idProofDoc) fd.append("idProofDoc", newFiles.idProofDoc);
    if (newFiles.passportPhoto) fd.append("passportPhoto", newFiles.passportPhoto);
    if (newFiles.signature) fd.append("signature", newFiles.signature);

    try {
      await adminApi.put(`/admin/student-registrations/${editId}`, fd);
      showToast("success", "Student registration updated.");
      setShowEdit(false);
      fetchStudents();
    } catch (err) {
      setEditError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setEditSaving(false);
    }
  };

  // Edit-form field helpers (keep the modal markup compact)
  const txt = (key, extra = {}) => (
    <input
      className={inputCls}
      value={editForm[key]}
      onChange={(e) => setF(key, e.target.value)}
      {...extra}
    />
  );

  const area = (key) => (
    <textarea
      rows={2}
      className={inputCls}
      value={editForm[key]}
      onChange={(e) => setF(key, e.target.value)}
    />
  );

  const sel = (key, items) => (
    <select
      className={inputCls}
      value={editForm[key]}
      onChange={(e) => setF(key, e.target.value)}
    >
      <option value="">-- Select --</option>
      {items.map((it) => (
        <option key={it.value} value={it.value}>
          {it.label}
        </option>
      ))}
    </select>
  );

  const fileField = (inputRef, key, accept) => (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) =>
          setNewFiles((p) => ({ ...p, [key]: e.target.files?.[0] || null }))
        }
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="text-xs border border-slate-200 rounded-lg px-3 py-2 hover:border-[#0EA5A4] transition"
      >
        {newFiles[key] ? newFiles[key].name : "Replace file"}
      </button>

      {existingFiles[key] && !newFiles[key] && (
        <a
          href={fileUrl(existingFiles[key])}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0EA5A4] text-xs inline-flex items-center gap-1"
        >
          <ExternalLink size={12} />
          View
        </a>
      )}
    </div>
  );

  const courseItems = meta.courses.map((c) => ({ value: c.crs_id, label: c.course_name }));
  const deptItems = meta.departments.map((d) => ({ value: d.dept_id, label: d.dept_name }));
  const admCatItems = meta.admissionCategories.map((c) => ({ value: c.parid, label: c.par_value }));
  const otherCatItems = meta.otherCategories.map((c) => ({ value: c.parid, label: c.par_value }));

  // ── View / Print modal ───────────────────────────────────────────────────
  const [showView, setShowView] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  const openView = async (regId) => {
    setShowView(true);
    setViewLoading(true);
    setViewData(null);

    try {
      const res = await adminApi.get(`/admin/student-registrations/${regId}`);
      setViewData(res.data.student);
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not load this registration.");
      setShowView(false);
    } finally {
      setViewLoading(false);
    }
  };

  useEffect(() => {
    if (viewData && autoPrint) {
      setAutoPrint(false);
      const t = setTimeout(() => window.print(), 250);
      return () => clearTimeout(t);
    }
  }, [viewData, autoPrint]);

  const handlePrint = () => window.print();

  return (
    <div className="h-full flex flex-col">
      {/* Print-only CSS */}
      {showView && (
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

          @media print {
            /* margin:0 removes the browser's own header/footer (date, title, URL, page no.).
               The page margins are recreated with padding on .print-portal below. */
            @page { size: A4; margin: 0; }

            html, body {
              width: 210mm !important;
              height: auto !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: visible !important;
              background: #fff !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            /* Hide the whole app (it takes no space) — only the portal with the form prints */
            body > *:not(.print-portal) { display: none !important; }
            .print-portal {
              display: block !important;
              width: 210mm !important;
              box-sizing: border-box !important;
              padding: 14mm 10mm 10mm !important;
              margin: 0 !important;
            }

            .print-modal-backdrop {
              position: static !important; display: block !important;
              width: 100% !important; height: auto !important; min-height: 0 !important;
              padding: 0 !important; margin: 0 !important;
              background: #fff !important; overflow: visible !important;
              backdrop-filter: none !important; -webkit-backdrop-filter: none !important;
              opacity: 1 !important;
            }

            .print-modal-card {
              position: static !important; display: block !important;
              width: 100% !important; max-width: none !important;
              height: auto !important; max-height: none !important; min-height: 0 !important;
              margin: 0 !important; padding: 0 !important; overflow: visible !important;
              background: #fff !important; box-shadow: none !important; border-radius: 0 !important;
              transform: none !important; opacity: 1 !important;
            }

            .print-area {
              position: static !important; display: block !important;
              width: 100% !important; max-width: none !important;
              height: auto !important; max-height: none !important; min-height: 0 !important;
              margin: 0 !important; padding: 0 !important; overflow: visible !important;
              background: #fff !important;
            }

            .no-print { display: none !important; }

            /* Spacing tuned so the whole form sits comfortably on one A4 page */
            .pp-a4 {
              border: none !important; border-radius: 0 !important;
              max-width: none !important; width: 100% !important;
              padding: 0 !important; margin: 0 !important;
              font-size: 9.5pt !important; overflow: visible !important;
            }
            .pp-header { padding-bottom: 7px !important; margin-bottom: 6px !important; }
            .pp-title-bar { padding: 8px 16px !important; margin-bottom: 6px !important; }
            .pp-regid { margin-bottom: 7px !important; }
            .pp-student-banner { padding: 9px 14px !important; margin-bottom: 7px !important; }
            .pp-photo-box { width: 74px !important; height: 92px !important; }
            .pp-section { margin-bottom: 6px !important; }
            .pp-sec-head { padding: 5px 11px !important; }
            .pp-sec-body { padding: 9px 11px !important; }
            .pp-info-grid { gap: 8px 16px !important; }
            .pp-exam-table th, .pp-exam-table td { padding: 6px 8px !important; }
            .pp-sig-section { margin-top: 16px !important; padding-top: 8px !important; }
            .pp-sig-line { margin-top: 12px !important; }
            .pp-doc-footer { margin-top: 8px !important; padding-top: 6px !important; }
          }

          /* ── Printable registration form (same design as the student-end print) ── */
          .pp-a4{
            max-width:794px;margin:0 auto;background:#fff;
            border:1px solid #cbd5e1;border-radius:4px;
            padding:32px 36px 28px;
            font-family:'DM Sans',Arial,sans-serif;font-size:10pt;color:#184381;
            line-height:1.45;position:relative;overflow:hidden;
            -webkit-print-color-adjust:exact;print-color-adjust:exact;
          }
          .pp-watermark{
            position:absolute;top:50%;left:50%;
            transform:translate(-50%,-50%) rotate(-35deg);
            font-family:'EB Garamond',serif;font-size:120pt;color:rgba(10,30,80,0.04);
            pointer-events:none;white-space:nowrap;z-index:0;
            font-weight:700;letter-spacing:0.1em;user-select:none;
          }
          .pp-header{
            display:flex;align-items:center;gap:14px;
            padding-bottom:7px;border-bottom:3px solid #184381;
            margin-bottom:5px;position:relative;z-index:1;
          }
          .pp-logo{width:62px;height:62px;object-fit:contain;flex-shrink:0;}
          .pp-header-mid{flex:1;}
          .pp-college-name{font-family:'EB Garamond',serif;font-size:17pt;font-weight:700;color:#184381;line-height:1.15;}
          .pp-college-odia{font-size:8.5pt;font-weight:600;color:#4a6fa5;margin-top:2px;}
          .pp-college-sub{font-size:8pt;color:#184381;margin-top:3px;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;}
          .pp-header-right{text-align:right;flex-shrink:0;}
          .pp-print-date{font-size:7.5pt;color:#4a6fa5;}
          .pp-title-bar{
            background:linear-gradient(135deg,#184381 0%,#2b68c3 100%);
            color:#fff;text-align:center;padding:10px 20px;
            border-radius:7px;margin-bottom:7px;position:relative;z-index:1;
          }
          .pp-title-main{font-family:'EB Garamond',serif;font-size:14pt;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;}
          .pp-regid{
            text-align:center;margin:0 0 8px;position:relative;z-index:1;
            font-size:9.5pt;color:#4a6fa5;font-weight:600;letter-spacing:0.03em;
          }
          .pp-regid strong{color:#184381;font-size:11pt;letter-spacing:0.08em;text-decoration:underline;}
          .pp-regid span{font-weight:500;font-size:8.5pt;margin-left:10px;}
          .pp-student-banner{
            display:flex;align-items:center;gap:16px;
            background:#eaf1fb;border:1.5px solid #c7d6ec;
            border-left:5px solid #184381;border-radius:8px;
            padding:10px 16px;margin-bottom:7px;position:relative;z-index:1;
          }
          .pp-photo-box{
            width:80px;height:100px;flex-shrink:0;
            border:2px solid #c7d6ec;border-radius:5px;
            background:#c7d6ec;display:flex;align-items:center;justify-content:center;overflow:hidden;
          }
          .pp-photo-box img{width:100%;height:100%;object-fit:cover;}
          .pp-photo-placeholder{font-size:7.5pt;color:#6e93c9;text-align:center;padding:6px;}
          .pp-student-grid{flex:1;display:grid;grid-template-columns:repeat(3,1fr);gap:8px 16px;}
          .pp-si-item{display:flex;flex-direction:column;gap:2px;}
          .pp-si-label{font-size:6.5pt;font-weight:700;color:#4a6fa5;text-transform:uppercase;letter-spacing:0.07em;}
          .pp-si-val{font-size:9.5pt;font-weight:600;color:#184381;word-break:break-word;}
          .pp-sig-box{width:90px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;gap:5px;}
          .pp-sig-img-box{
            width:90px;height:42px;border:1.5px solid #c7d6ec;border-radius:4px;background:#fff;
            display:flex;align-items:center;justify-content:center;overflow:hidden;
          }
          .pp-sig-img-box img{width:100%;height:100%;object-fit:contain;}
          .pp-sig-placeholder{font-size:7pt;color:#6e93c9;}
          .pp-sig-label{font-size:7pt;color:#4a6fa5;text-align:center;}
          .pp-section{margin-bottom:5px;position:relative;z-index:1;page-break-inside:avoid;break-inside:avoid;}
          .pp-sec-head{
            background:#184381;color:#fff;padding:6px 13px;border-radius:5px 5px 0 0;
            font-size:8pt;font-weight:700;letter-spacing:0.09em;text-transform:uppercase;
          }
          .pp-sec-body{border:1px solid #c7d6ec;border-top:none;border-radius:0 0 5px 5px;padding:11px 13px;background:#fff;}
          .pp-info-grid{display:grid;gap:10px 18px;}
          .pp-cols-3{grid-template-columns:repeat(3,1fr);}
          .pp-cols-4{grid-template-columns:repeat(4,1fr);}
          .pp-info-item{display:flex;flex-direction:column;gap:2px;}
          .pp-info-label{font-size:6.5pt;font-weight:700;color:#4a6fa5;text-transform:uppercase;letter-spacing:0.07em;}
          .pp-info-val{font-size:9.5pt;font-weight:500;color:#184381;border-bottom:1px solid #c7d6ec;padding-bottom:3px;word-break:break-word;}
          .pp-info-val.highlight{color:#184381;font-weight:700;}
          .pill-blue{display:inline-block;background:#dbeafe;color:#184381;padding:2px 9px;border-radius:20px;font-size:8pt;font-weight:700;}
          .pill-green{display:inline-block;background:#e8f5e9;color:#2e7d32;padding:2px 9px;border-radius:20px;font-size:8pt;font-weight:700;}
          .pill-gold{display:inline-block;background:#fff8e1;color:#7a5800;padding:2px 9px;border-radius:20px;font-size:8pt;font-weight:700;}
          .pp-address-split{display:grid;grid-template-columns:1fr 1fr;gap:18px;}
          .pp-addr-title{font-size:7.5pt;font-weight:700;color:#184381;letter-spacing:0.07em;text-transform:uppercase;margin-bottom:7px;padding-bottom:4px;border-bottom:1px dashed #c7d6ec;}
          .pp-addr-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:8px;}
          .pp-exam-table{width:100%;border-collapse:collapse;font-size:8.5pt;}
          .pp-exam-table thead tr{background:#eaf1fb;}
          .pp-exam-table th{text-align:left;padding:7px 10px;font-size:7pt;font-weight:700;color:#4a6fa5;text-transform:uppercase;letter-spacing:0.06em;border-bottom:2px solid #c7d6ec;}
          .pp-exam-table td{padding:7px 10px;border-bottom:1px solid #c7d6ec;color:#184381;}
          .pp-exam-table tr:last-child td{border-bottom:none;}
          .pp-exam-table tbody tr:nth-child(even){background:#eaf1fb;}
          .pp-sig-section{
            display:flex;justify-content:space-between;align-items:flex-end;
            margin-top:20px;padding-top:14px;border-top:1.5px dashed #c7d6ec;position:relative;z-index:1;
          }
          .pp-sig-block{text-align:center;width:150px;}
          .pp-sig-line{border-top:1.5px solid #184381;margin-bottom:4px;margin-top:15px;}
          .pp-sig-block p{font-size:7.5pt;color:#4a6fa5;}
          .pp-doc-footer{
            margin-top:10px;padding-top:8px;border-top:1.5px solid #c7d6ec;
            display:flex;justify-content:space-between;font-size:7pt;color:#4a6fa5;position:relative;z-index:1;
          }
          .pp-doc-footer strong{color:#184381;}

          @media screen and (max-width: 768px) {
            .pp-a4{padding:16px;}
            .pp-student-banner{flex-wrap:wrap;}
            .pp-cols-3,.pp-cols-4,.pp-student-grid,.pp-address-split{grid-template-columns:1fr 1fr;}
          }
        `}</style>
      )}

      {/* Toolbar */}
      <div className="shrink-0 mb-2">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div>
            <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
              Manage Student Registration
            </h3>
          </div>

          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 bg-[#0B2545] hover:bg-[#143A63] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Download size={15} />
            Export to Excel
          </button>
        </div>

        {/* Search / filter bar */}
        <form
          onSubmit={handleSearch}
          className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex flex-wrap items-end gap-3"
        >
          <div className="flex-1 min-w-[150px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Student Name</label>

            <div className="relative">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300"
              />

              <input
                value={nameQ}
                onChange={(e) => setNameQ(e.target.value)}
                placeholder="Search by name..."
                className="w-full border border-slate-200 rounded-lg pl-7 pr-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
              />
            </div>
          </div>

          <div className="min-w-[140px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Course</label>

            <select value={courseQ} onChange={(e) => setCourseQ(e.target.value)} className={inputCls}>
              <option value="">All Courses</option>
              {meta.courses.map((c) => (
                <option key={c.crs_id} value={c.crs_id}>
                  {c.course_name}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[110px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Year of Admission</label>

            <select value={yearQ} onChange={(e) => setYearQ(e.target.value)} className={inputCls}>
              <option value="">All Years</option>
              {ADMISSION_YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[130px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Created From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={inputCls}
            />
          </div>

          <div className="min-w-[130px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Created To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={inputCls}
            />
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="submit"
              className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
            >
              <Search size={13} />
              Search
            </button>

            <button
              type="button"
              onClick={handleReset}
              title="Reset filters"
              className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 text-[13px] px-1.5 py-1.5 rounded-lg transition"
            >
              <RotateCcw size={13} />
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" />
            Loading student registrations...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : students.length === 0 ? (
          <div className="py-16 text-center">
            <UserCircle2 size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No student registrations match your filters.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
                <tr className="text-left text-sm">
                  <th className="th-colorful px-4 py-1.5 font-bold uppercase tracking-wide w-12 border-b-2 border-r">
                    Sl No
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">
                    Student Name
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">
                    Gender
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">
                    Course
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">
                    Year of Admission
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">
                    Created Date
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">
                    Registration ID
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide text-center border-b-2 border-r">
                    Status
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide text-center border-b-2 border-r">
                    Mail
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide text-center border-b-2">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                <AnimatePresence initial={false}>
                  {students.map((s, i) => (
                    <motion.tr
                      key={s.reg_id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.015 }}
                      className="hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200"
                    >
                      <td className="px-4 py-2 text-slate-400 border-r border-slate-200">{i + 1}</td>

                      <td className="px-3 py-2 border-r border-slate-200">
                        <div className="flex items-center gap-2.5">
                          {s.st_photo ? (
                            <img
                              src={fileUrl(s.st_photo)}
                              alt={s.st_name}
                              className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <UserCircle2 size={18} className="text-slate-300" />
                            </div>
                          )}

                          <span className="text-slate-800 font-medium">{s.st_name || "—"}</span>
                        </div>
                      </td>

                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">
                        {s.gender_name || "—"}
                      </td>

                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">
                        {s.course_name || "—"}
                      </td>

                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">
                        {s.admission_year || "—"}
                      </td>

                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">
                        {fmtDate(s.created_date)}
                      </td>

                      <td className="px-3 py-2 text-slate-500 font-mono text-xs border-r border-slate-200">
                        {s.referance_number || "—"}
                      </td>

                      {/* STATUS */}
                      <td className="px-3 py-2 border-r border-slate-200">
                        <div className="flex items-center justify-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusClass(
                              s
                            )}`}
                          >
                            {statusLabel(s)}
                          </span>
                        </div>
                      </td>

                      {/* MAIL */}
                      <td className="px-3 py-2 border-r border-slate-200">
                        <div className="flex items-center justify-center">
                          <ActionBtn
                            title="Send Mail"
                            tone="blue"
                            onClick={(e) => openMailModal(s, e)}
                          >
                            <Mail size={15} />
                          </ActionBtn>
                        </div>
                      </td>

                      {/* ACTIONS */}
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1.5">
                          {/* EDIT — hidden once active */}
                          {!isActive(s) && (
                            <ActionBtn
                              title="Edit"
                              tone="amber"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(s.reg_id);
                              }}
                            >
                              <Pencil size={15} />
                            </ActionBtn>
                          )}

                          {/* VIEW */}
                          <ActionBtn
                            title="View"
                            tone="indigo"
                            onClick={(e) => {
                              e.stopPropagation();
                              openView(s.reg_id);
                            }}
                          >
                            <Eye size={15} />
                          </ActionBtn>

                          {/* PRINT / ACTIVATE */}
                          {isActive(s) ? (
                            <ActionBtn
                              title="Print"
                              tone="teal"
                              onClick={(e) => {
                                e.stopPropagation();
                                openViewAndPrint(s.reg_id);
                              }}
                            >
                              <Printer size={15} />
                            </ActionBtn>
                          ) : isIncomplete(s) ? (
                            <ActionBtn
                              title="Not available — student has not submitted Step 2"
                              tone="slate"
                              disabled
                            >
                              <BadgeCheck size={15} />
                            </ActionBtn>
                          ) : (
                            <ActionBtn
                              title="Activate"
                              tone="green"
                              disabled={activatingId === s.reg_id}
                              onClick={(e) => handleActivate(s, e)}
                            >
                              {activatingId === s.reg_id ? (
                                <Loader2 size={15} className="animate-spin" />
                              ) : (
                                <BadgeCheck size={15} />
                              )}
                            </ActionBtn>
                          )}

                          {/* DELETE — hidden once active */}
                          {!isActive(s) && (
                            <ActionBtn
                              title="Delete"
                              tone="red"
                              disabled={deletingId === s.reg_id}
                              onClick={(e) => handleDelete(s, e)}
                            >
                              {deletingId === s.reg_id ? (
                                <Loader2 size={15} className="animate-spin" />
                              ) : (
                                <Trash2 size={15} />
                              )}
                            </ActionBtn>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── MAIL MODAL ─────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {mailStudent && (
          <SendMailModal
            key={mailStudent.reg_id}
            student={mailStudent}
            senderMail={meta.senderMail}
            onClose={closeMailModal}
            onSent={(msg) => showToast("success", msg)}
          />
        )}
      </AnimatePresence>

      {/* ── Edit modal ────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showEdit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeEdit}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                <h4 className="font-display font-semibold text-[#0B2545]">
                  Edit Student Registration
                </h4>

                <button
                  type="button"
                  onClick={closeEdit}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <X size={18} />
                </button>
              </div>

              {editLoading ? (
                <div className="flex items-center justify-center py-20 text-slate-400 text-sm gap-2">
                  <Loader2 size={18} className="animate-spin" />
                  Loading...
                </div>
              ) : (
                <form onSubmit={handleEditSubmit} className="overflow-y-auto px-6 py-5 space-y-6">
                  {/* Basic details */}
                  <div>
                    <SectionTitle>Basic Details</SectionTitle>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Student Name" req>
                        {txt("st_name")}
                      </Field>
                      <Field label="Gender" req>
                        {sel("st_gender", GENDERS)}
                      </Field>
                      <Field label="Date of Birth" req>
                        {txt("st_dob", { type: "date" })}
                      </Field>
                      <Field label="ID Proof Type" req>
                        {sel("st_idprf_type", meta.idProofTypes)}
                      </Field>
                      <Field label="ID Proof No" req>
                        {txt("st_aadharno")}
                      </Field>
                      <Field label="ID Proof Document" req>
                        {fileField(idProofRef, "idProofDoc", ".jpg,.jpeg,.png,.pdf")}
                      </Field>
                    </div>
                  </div>

                  {/* Course details */}
                  <div>
                    <SectionTitle>Course Details</SectionTitle>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Course" req>
                        {sel("st_course", courseItems)}
                      </Field>
                      <Field label="Department" req>
                        {sel("st_department", deptItems)}
                      </Field>
                      <Field label="Year of Admission" req>
                        {sel("admission_year", YEAR_OPTIONS)}
                      </Field>
                      <Field label="NEET Roll No / Regd No" req>
                        {txt("st_rollno")}
                      </Field>
                      <Field label="Admission Category" req>
                        {sel("st_admsn_category", admCatItems)}
                      </Field>
                      <Field label="Other Category" req>
                        {sel("st_admsn_othr_categry", otherCatItems)}
                      </Field>
                    </div>
                  </div>

                  {/* Personal details */}
                  <div>
                    <SectionTitle>Personal Details</SectionTitle>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Blood Group" req>
                        {sel("st_bloodgroup", meta.bloodGroups)}
                      </Field>
                      <Field label="Passport Photo" req>
                        {fileField(photoRef, "passportPhoto", ".jpg,.jpeg,.png")}
                      </Field>
                      <Field label="Signature" req>
                        {fileField(signatureRef, "signature", ".jpg,.jpeg,.png")}
                      </Field>
                      <Field label="Father's Name" req>
                        {txt("father_name")}
                      </Field>
                      <Field label="Mother's Name" req>
                        {txt("mother_name")}
                      </Field>
                      <Field label="Father's Occupation" req>
                        {txt("father_occupation")}
                      </Field>
                      <Field label="Mother's Occupation" req>
                        {txt("mother_occupation")}
                      </Field>
                      <Field label="Reservation Category" req>
                        {sel("category", meta.reservationCategories)}
                      </Field>
                      <Field label="Sub Category" req>
                        {sel("sub_catagory", meta.subCategories)}
                      </Field>
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <SectionTitle>Address</SectionTitle>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Present Address" req span={2}>
                        {area("pre_address")}
                      </Field>
                      <Field label="Present State" req>
                        {sel("pre_state", STATE_OPTIONS)}
                      </Field>
                      <Field label="Present Pin" req>
                        {txt("pre_pin")}
                      </Field>
                      <Field label="Permanent Address" req span={2}>
                        {area("per_address")}
                      </Field>
                      <Field label="Permanent State" req>
                        {sel("per_state", STATE_OPTIONS)}
                      </Field>
                      <Field label="Permanent Pin" req>
                        {txt("per_pin")}
                      </Field>
                    </div>
                  </div>

                  {/* Contact */}
                  <div>
                    <SectionTitle>Contact Details</SectionTitle>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Student's Phone" req>
                        {txt("st_phno")}
                      </Field>
                      <Field label="Student's Email" req>
                        {txt("st_mailid")}
                      </Field>
                      <Field label="Landline of Parents" req>
                        {txt("landline_parent")}
                      </Field>
                      <Field label="Mobile of Parents" req>
                        {txt("phno_parent")}
                      </Field>
                      <Field label="Email of Parents" req>
                        {txt("mailid_parent")}
                      </Field>
                    </div>
                  </div>

                  {/* Qualifying exam */}
                  <div>
                    <SectionTitle>Last Qualifying Examination</SectionTitle>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Examination" req>
                        {txt("st_examination")}
                      </Field>
                      <Field label="Year of Passing" req>
                        {txt("passing_year")}
                      </Field>
                      <Field label="College Name" req>
                        {txt("college_name")}
                      </Field>
                      <Field label="Board/University" req>
                        {txt("university_name")}
                      </Field>
                      <Field label="Remarks" req span={2}>
                        {area("remarks")}
                      </Field>
                    </div>
                  </div>

                  {/* Secondary exam */}
                  <div>
                    <SectionTitle>Secondary Examination (optional)</SectionTitle>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Examination" req>
                        {txt("secexam_type")}
                      </Field>
                      <Field label="Year of Passing" req>
                        {txt("secpass_year")}
                      </Field>
                      <Field label="College Name" req>
                        {txt("sec_col_name")}
                      </Field>
                      <Field label="Board/University" req>
                        {txt("sec_board")}
                      </Field>
                      <Field label="Remarks" req span={2}>
                        {area("sec_remark")}
                      </Field>
                    </div>
                  </div>

                  {editError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                      {editError}
                    </p>
                  )}

                  <div className="flex items-center gap-3 pt-2 pb-1 sticky bottom-0 bg-white">
                    <button
                      type="submit"
                      disabled={editSaving}
                      className="flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg px-6 py-2.5 transition disabled:opacity-60"
                    >
                      {editSaving ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Pencil size={15} />
                      )}

                      {editSaving ? "Saving..." : "Update"}
                    </button>

                    <button
                      type="button"
                      onClick={closeEdit}
                      className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── View modal ─────────────────────────────────────────────────────── */}
      {createPortal(
        <div className="print-portal">
        <AnimatePresence>
          {showView && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowView(false)}
              className="print-modal-backdrop fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.97, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 12 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                className="print-modal-card bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
              >
                <div className="flex items-center justify-between px-6 py-3.5 border-b border-slate-100 shrink-0 no-print bg-gradient-to-r from-slate-50 to-white">
                  <h4 className="font-display font-semibold text-[#0B2545]">
                    View Student Registration
                  </h4>

                  <div className="flex items-center gap-2.5">
                    {viewData && (
                      <button
                        type="button"
                        onClick={handlePrint}
                        title="Print this registration form"
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] text-white text-[13px] font-semibold tracking-wide px-4 py-2 rounded-lg shadow-md shadow-[#0B2545]/20 ring-1 ring-inset ring-white/10 hover:brightness-110 hover:shadow-lg hover:-translate-y-px active:translate-y-0 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0EA5A4]/50 transition-all duration-150"
                      >
                        <Printer size={15} />
                        Print
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => setShowView(false)}
                      title="Close"
                      aria-label="Close"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors duration-150"
                    >
                      <X size={17} />
                    </button>
                  </div>
                </div>

                {viewLoading ? (
                  <div className="flex items-center justify-center py-20 text-slate-400 text-sm gap-2">
                    <Loader2 size={18} className="animate-spin" />
                    Loading...
                  </div>
                ) : viewData ? (
                  <div className="overflow-y-auto px-6 py-5 print-area">
                    <div className="pp-a4">
                      <div className="pp-watermark">MKCG</div>

                      {/* Header */}
                      <div className="pp-header">
                        <img className="pp-logo" src={mkcgLogo} alt="MKCG Logo" />
                        <div className="pp-header-mid">
                          <div className="pp-college-name">M.K.C.G. Medical College</div>
                          <div className="pp-college-sub">Brahmapur, Odisha, India</div>
                          <div className="pp-college-odia">
                            ମହାରାଜା କୃଷ୍ଣଚନ୍ଦ୍ର ଗଜପତି ଭେଷଜ ମହାବିଦ୍ୟାଳୟ, ବ୍ରହ୍ମପୁର, ଓଡ଼ିଶା, ଭାରତ
                          </div>
                        </div>
                        <div className="pp-header-right">
                          <div className="pp-print-date">
                            Printed: {new Date().toLocaleDateString("en-IN", { dateStyle: "long" })}
                          </div>
                        </div>
                      </div>

                      {/* Title */}
                      <div className="pp-title-bar">
                        <div className="pp-title-main">Student's Biodata</div>
                      </div>

                      {/* Registration ID — generated when the admin activates the registration */}
                      {viewData.referance_number && Number(viewData.status) === 1 && (
                        <div className="pp-regid">
                          Registration ID: <strong>{viewData.referance_number}</strong>
                          {viewData.activate_date && (
                            <span>Activated on: {fmtDate(viewData.activate_date)}</span>
                          )}
                        </div>
                      )}

                      {/* Student banner */}
                      <div className="pp-student-banner">
                        <div className="pp-photo-box">
                          {viewData.st_photo ? (
                            <img src={fileUrl(viewData.st_photo)} alt="Passport" />
                          ) : (
                            <span className="pp-photo-placeholder">
                              📷<br />Passport<br />Photo
                            </span>
                          )}
                        </div>

                        <div className="pp-student-grid">
                          {[
                            ["Full Name", viewData.st_name],
                            ["Email Address", viewData.st_mailid],
                            ["Mobile", viewData.st_phno],
                            ["Gender", viewData.gender_name],
                            ["Date of Birth", fmtDate(viewData.st_dob)],
                            ["Blood Group", viewData.bloodgroup_name],
                          ].map(([label, val]) => (
                            <div key={label} className="pp-si-item">
                              <span className="pp-si-label">{label}</span>
                              <span className={`pp-si-val${label === "Blood Group" ? " pill-blue" : ""}`}>
                                {val || "—"}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="pp-sig-box">
                          <div className="pp-sig-img-box">
                            {viewData.st_signature ? (
                              <img src={fileUrl(viewData.st_signature)} alt="Signature" />
                            ) : (
                              <span className="pp-sig-placeholder">Signature</span>
                            )}
                          </div>
                          <span className="pp-sig-label">Student Signature</span>
                        </div>
                      </div>

                      {/* Info sections */}
                      {[
                        {
                          icon: "🎓",
                          title: "Academic Information",
                          cols: 4,
                          items: [
                            ["Course", viewData.course_name, "highlight"],
                            ["Year of Admission", viewData.admission_year],
                            ["NEET Roll No. / Reg. No.", viewData.st_rollno],
                            ["Reservation Category", viewData.category_name, "pill-gold"],
                            ["Sub Category", viewData.subcategory_name],
                            ...(viewData.dept_name ? [["Department", viewData.dept_name]] : []),
                            ...(viewData.admsn_category_label
                              ? [["Admission Category", viewData.admsn_category_label, "pill-gold"]]
                              : []),
                            ...(viewData.othr_category_label
                              ? [["Category", viewData.othr_category_label]]
                              : []),
                          ],
                        },
                        {
                          icon: "🆔",
                          title: "Identity Proof",
                          cols: 3,
                          items: [
                            ["ID Proof Type", viewData.idprf_type_name],
                            ["ID Proof Number", viewData.st_aadharno, "highlight"],
                            [
                              "Document Status",
                              viewData.st_idproof_img ? "✓ Document Attached" : "Not attached",
                              viewData.st_idproof_img ? "pill-green" : "",
                            ],
                          ],
                        },
                        {
                          icon: "👨‍👩‍👦",
                          title: "Family Information",
                          cols: 3,
                          items: [
                            ["Father's Name", viewData.father_name],
                            ["Father's Occupation", viewData.father_occupation],
                            ["Parent Mobile", viewData.phno_parent],
                            ["Mother's Name", viewData.mother_name],
                            ["Mother's Occupation", viewData.mother_occupation],
                            ["Parent Email", viewData.mailid_parent],
                          ],
                        },
                      ].map((sec) => (
                        <div key={sec.title} className="pp-section">
                          <div className="pp-sec-head">
                            {sec.icon}&nbsp; {sec.title}
                          </div>
                          <div className="pp-sec-body">
                            <div className={`pp-info-grid pp-cols-${sec.cols}`}>
                              {sec.items.map(([label, val, cls]) => (
                                <div key={label} className="pp-info-item">
                                  <span className="pp-info-label">{label}</span>
                                  <span className={`pp-info-val ${cls || ""}`}>{val || "—"}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Address */}
                      <div className="pp-section">
                        <div className="pp-sec-head">📍&nbsp; Address Details</div>
                        <div className="pp-sec-body">
                          <div className="pp-address-split">
                            {[
                              ["Present Address", viewData.pre_address, viewData.pre_state, viewData.pre_pin],
                              ["Permanent Address", viewData.per_address, viewData.per_state, viewData.per_pin],
                            ].map(([title, addr, state, pin]) => (
                              <div key={title}>
                                <div className="pp-addr-title">{title}</div>
                                <div className="pp-info-item">
                                  <span className="pp-info-label">Address</span>
                                  <span className="pp-info-val">{addr || "—"}</span>
                                </div>
                                <div className="pp-addr-row">
                                  <div className="pp-info-item">
                                    <span className="pp-info-label">State</span>
                                    <span className="pp-info-val">{state || "—"}</span>
                                  </div>
                                  <div className="pp-info-item">
                                    <span className="pp-info-label">PIN Code</span>
                                    <span className="pp-info-val">{pin || "—"}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Qualifying examination */}
                      <div className="pp-section">
                        <div className="pp-sec-head">📄&nbsp; Last Qualifying Examination Details</div>
                        <div className="pp-sec-body" style={{ padding: 0 }}>
                          <table className="pp-exam-table">
                            <thead>
                              <tr>
                                {["#", "Examination", "Year of Passing", "Name of College", "Board / University", "Remarks"].map(
                                  (h) => (
                                    <th key={h}>{h}</th>
                                  )
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>1</td>
                                <td>{viewData.st_examination || "—"}</td>
                                <td>{viewData.passing_year || "—"}</td>
                                <td>{viewData.college_name || "—"}</td>
                                <td>{viewData.university_name || "—"}</td>
                                <td>{viewData.remarks || "—"}</td>
                              </tr>

                              {(viewData.secexam_type || viewData.sec_col_name) && (
                                <tr>
                                  <td>2</td>
                                  <td>{viewData.secexam_type || "—"}</td>
                                  <td>{viewData.secpass_year || "—"}</td>
                                  <td>{viewData.sec_col_name || "—"}</td>
                                  <td>{viewData.sec_board || "—"}</td>
                                  <td>{viewData.sec_remark || "—"}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Signatures */}
                      <div className="pp-sig-section">
                        <div className="pp-sig-block">
                          <div className="pp-sig-line" />
                          <p>Signature of Parents</p>
                        </div>
                        <div className="pp-sig-block">
                          <div className="pp-sig-line" />
                          <p>Signature of Student</p>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="pp-doc-footer">
                        <span>
                          All Rights Reserved © MKCG Medical College, Brahmapur, Odisha — {new Date().getFullYear()}
                        </span>
                        <span>
                          Powered by <strong>Orisys Infotech</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>,
        document.body
      )}
    </div>
  );
}
